/**
 * TransportRuleModal Component
 *
 * Modal dialog for creating or editing Exchange transport rules.
 *
 * Features:
 * - Create/Edit mode support
 * - Rule type selection (block-external-senders, block-auto-forward, block-client-domain)
 * - Conditional client domain field for block-client-domain type
 * - Priority configuration
 * - Enabled/Disabled toggle
 * - Form validation
 * - Auto-close on successful submit
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Dropdown,
  Option,
  Input,
  Spinner,
  Text,
  Switch,
  tokens,
} from '@fluentui/react-components';
import type { TransportRuleType, ExchangeRuleRequest, ExchangeRuleUpdateRequest } from '../../../types/transportRules';
import { TRANSPORT_RULE_TYPE_DISPLAY_NAMES } from '../../../types/transportRules';

interface TransportRuleModalProps {
  mode: 'create' | 'edit';
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ExchangeRuleRequest | ExchangeRuleUpdateRequest, ruleId?: string) => Promise<void>;
  initialData?: TransportRuleInitialData;
  totalRuleCount: number;  // Total number of transport rules for priority validation
}

/**
 * Initial data for edit mode
 */
export interface TransportRuleInitialData {
  ruleId: string;  // Rule Identity from Exchange Online
  ruleType: TransportRuleType;
  name?: string;  // Optional custom name
  clientDomain?: string;
  allowedDomains?: string[];  // Allowed domains for block-external-senders rule
  priority: number;
  enabled: boolean;
}

/**
 * @deprecated Use ExchangeRuleRequest/ExchangeRuleUpdateRequest instead
 * Legacy form data type - kept for backward compatibility
 */
export interface TransportRuleFormData {
  ruleId?: string;
  ruleType: TransportRuleType;
  clientDomain?: string;
  priority: number;
  enabled?: boolean;
}

export const TransportRuleModal: React.FC<TransportRuleModalProps> = ({
  mode,
  isOpen,
  onClose,
  onSubmit,
  initialData,
  totalRuleCount,
}) => {
  const [ruleId, setRuleId] = useState<string | undefined>(undefined);
  const [ruleType, setRuleType] = useState<TransportRuleType | ''>('');
  const [clientDomain, setClientDomain] = useState('');
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [priority, setPriority] = useState(0);
  const [enabled, setEnabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with initialData when modal opens in edit mode
  useEffect(() => {
    if (isOpen && mode === 'edit' && initialData) {
      setRuleId(initialData.ruleId);
      setRuleType(initialData.ruleType);
      setClientDomain(initialData.clientDomain || '');
      setAllowedDomains(initialData.allowedDomains || []);
      setPriority(initialData.priority);
      setEnabled(initialData.enabled ?? true);
    } else if (!isOpen) {
      // Reset form when modal closes
      setRuleId(undefined);
      setRuleType('');
      setClientDomain('');
      setAllowedDomains([]);
      setNewDomain('');
      setPriority(0);
      setEnabled(true);
      setIsSubmitting(false);
    }
  }, [isOpen, mode, initialData]);

  const handleAddDomain = () => {
    const trimmedDomain = newDomain.trim();
    if (trimmedDomain && !allowedDomains.includes(trimmedDomain)) {
      setAllowedDomains([...allowedDomains, trimmedDomain]);
      setNewDomain('');
    }
  };

  const handleRemoveDomain = (domainToRemove: string) => {
    setAllowedDomains(allowedDomains.filter(d => d !== domainToRemove));
  };

  const handleSubmit = async () => {
    // Validation
    if (!ruleType) {
      return;
    }

    if (ruleType === 'block-client-domain' && !clientDomain.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (mode === 'create') {
        // Create mode: Send ExchangeRuleRequest to POST endpoint
        const createRequest: ExchangeRuleRequest = {
          type: 'transport',
          rule_type: ruleType as TransportRuleType,
          priority,
          enabled,
        };

        if (ruleType === 'block-client-domain') {
          createRequest.configuration = {
            client_domain: clientDomain.trim()
          };
        }

        if (ruleType === 'block-external-senders' && allowedDomains.length > 0) {
          createRequest.configuration = {
            allowed_domains: allowedDomains
          };
        }

        await onSubmit(createRequest);
      } else {
        // Edit mode: Send ExchangeRuleUpdateRequest to PATCH endpoint
        const updateRequest: ExchangeRuleUpdateRequest = {
          priority,
          enabled,
        };

        // Include allowed_domains configuration for block-external-senders rule
        if (ruleType === 'block-external-senders') {
          updateRequest.configuration = {
            allowed_domains: allowedDomains
          };
        }

        await onSubmit(updateRequest, ruleId);
      }

      // Only close modal if submission succeeds
      onClose();
    } catch {
      // Modal stays open on error so user can retry or cancel
      // Error is handled by parent component via onSubmit rejection
    } finally {
      setIsSubmitting(false);
    }
  };

  const ruleTypes: TransportRuleType[] = [
    'block-external-senders',
    'block-auto-forward',
    'block-client-domain',
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{mode === 'edit' ? 'Edit Transport Rule' : 'Create Transport Rule'}</DialogTitle>
          <DialogContent>
            {isSubmitting ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: tokens.spacingVerticalL,
                padding: tokens.spacingVerticalXXL
              }}>
                <Spinner size="large" label={mode === 'edit' ? 'Updating transport rule...' : 'Creating transport rule...'} />
                <Text>This operation may take some time. Please wait...</Text>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Field label="Rule Type" required>
                  <Dropdown
                    aria-label="Rule Type"
                    placeholder="Select rule type"
                    value={ruleType ? TRANSPORT_RULE_TYPE_DISPLAY_NAMES[ruleType] : ''}
                    onOptionSelect={(_, data) => {
                      setRuleType(data.optionValue as TransportRuleType);
                    }}
                    disabled={mode === 'edit'}
                  >
                    {ruleTypes.map((type) => (
                      <Option key={type} value={type}>
                        {TRANSPORT_RULE_TYPE_DISPLAY_NAMES[type]}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>

                {ruleType === 'block-client-domain' && (
                  <Field label="Client Domain" required>
                    <Input
                      aria-label="Client Domain"
                      placeholder="e.g., client.com"
                      value={clientDomain}
                      onChange={(_, data) => setClientDomain(data.value)}
                    />
                  </Field>
                )}

                {ruleType === 'block-external-senders' && (
                  <Field label="Allowed Domains (Optional)">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                        Add domains that should be allowed to send email (all other external senders will be blocked)
                      </Text>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Input
                          aria-label="New Domain"
                          placeholder="e.g., lawfirm.com"
                          value={newDomain}
                          onChange={(_, data) => setNewDomain(data.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddDomain();
                            }
                          }}
                          style={{ flex: 1 }}
                        />
                        <Button
                          appearance="secondary"
                          onClick={handleAddDomain}
                          disabled={!newDomain.trim()}
                        >
                          Add
                        </Button>
                      </div>
                      {allowedDomains.length > 0 && (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          marginTop: '8px',
                          padding: '8px',
                          backgroundColor: tokens.colorNeutralBackground1,
                          borderRadius: tokens.borderRadiusMedium,
                          border: `1px solid ${tokens.colorNeutralStroke1}`
                        }}>
                          <Text weight="semibold" size={200}>Allowed Domains ({allowedDomains.length})</Text>
                          {allowedDomains.map((domain) => (
                            <div
                              key={domain}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 8px',
                                backgroundColor: tokens.colorNeutralBackground3,
                                borderRadius: tokens.borderRadiusSmall
                              }}
                            >
                              <Text>{domain}</Text>
                              <Button
                                appearance="subtle"
                                size="small"
                                onClick={() => handleRemoveDomain(domain)}
                              >
                                Remove
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Field>
                )}

                <Field label="Priority">
                  <Dropdown
                    aria-label="Priority"
                    placeholder="Select priority"
                    value={priority.toString()}
                    selectedOptions={[priority.toString()]}
                    onOptionSelect={(_, data) => {
                      setPriority(parseInt(data.optionValue || '0'));
                    }}
                  >
                    {Array.from({ length: mode === 'edit' ? totalRuleCount : totalRuleCount + 1 }, (_, i) => {
                      const label = `${i} ${i === 0 ? '(Highest Priority)' : i === (mode === 'edit' ? totalRuleCount - 1 : totalRuleCount) ? '(Lowest Priority)' : ''}`.trim();
                      return (
                        <Option key={i} value={i.toString()} text={label}>
                          {label}
                        </Option>
                      );
                    })}
                  </Dropdown>
                </Field>

                <Field label="Enabled">
                  <Switch
                    checked={enabled}
                    onChange={(_, data) => setEnabled(data.checked)}
                  />
                </Field>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !ruleType}
            >
              {isSubmitting
                ? (mode === 'edit' ? 'Saving...' : 'Creating...')
                : (mode === 'edit' ? 'Save' : 'Create')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
